import React, { useEffect, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { displayValue, isAccessRow } from './AuditTable';
import { apiFetch } from '../utils/apiAdmin';

/**
 * The right-hand slide-over: one entity's whole trail, or one actor's activity.
 *
 * ⚠️ THE SUMMARY IS SHOWN BESIDE THE EVENTS, NEVER INSTEAD OF THEM. A raw event
 * count is not a reschedule count - one live subtask carries ELEVEN due-date
 * events, mostly there-and-back inside a single afternoon of dragging a Gantt
 * bar. That is why `materialChangeCount` exists, why both numbers are displayed,
 * and why the warning below is not optional decoration.
 *
 * ⚠️ IT SUMMARISES THE LOADED WINDOW, and says so. The same honesty the page
 * applies to search applies here: a count over a 7-day window is a count over a
 * 7-day window, not the life of the task.
 */

/** Same rule as the layer's summariseEvents: same actor, same value back, inside 30 minutes. */
const REVERSAL_WINDOW_MS = 30 * 60 * 1000;

/**
 * One collapsible section of the slide-over.
 *
 * Open by default and persisted per section, like every other toggle in the
 * portal - a panel that reopens closed would hide the thing the reader came for.
 */
export function CollapsibleSection({ id, title, count, children }) {
    const storageKey = `adminAuditPanel:${id}`;
    const [open, setOpen] = useState(() => localStorage.getItem(storageKey) !== 'false');

    const toggle = () => {
        const next = !open;
        setOpen(next);
        localStorage.setItem(storageKey, String(next));
    };

    return (
        <section className="border-b border-gray-200">
            <button
                onClick={toggle}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                aria-expanded={open}
            >
                {open ? <FaChevronDown className="w-3 h-3 text-gray-400" /> : <FaChevronRight className="w-3 h-3 text-gray-400" />}
                <span>{title}</span>
                {count !== undefined && <span className="text-gray-400">({count})</span>}
            </button>
            {open && <div className="px-4 pb-3">{children}</div>}
        </section>
    );
}

/** The MFA states the sign-in route returns. `unknown` is a real answer here. */
const MFA_LABELS = {
    required: 'Required',
    enrolled: 'Enrolled',
    not_enrolled: 'Not enrolled',
    off: 'Off',
    unknown: 'Unknown'
};

/**
 * Per-field summary of the events shown.
 *
 * ⚠️ DELIBERATELY MIRRORS summariseEvents IN /opt/nodejs/auditResolve, including
 * the greedy chronological pairing - without that rule an alternating
 * A→B→A→B chain has more than one valid pairing and the count is not
 * deterministic. If that module's rule changes, this changes with it.
 */
export function summarise(events) {
    const byField = new Map();
    const chronological = [...events].sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));

    for (const event of chronological) {
        for (const change of event.fields || []) {
            if (!byField.has(change.field)) byField.set(change.field, []);
            byField.get(change.field).push({ ...change, event });
        }
    }

    const summary = [];
    for (const [field, changes] of byField) {
        const real = changes.filter(c => !c.noOp);
        const paired = new Set();

        real.forEach((change, i) => {
            if (paired.has(i)) return;
            for (let j = i + 1; j < real.length; j++) {
                if (paired.has(j)) continue;
                const undoes = JSON.stringify(real[j].after) === JSON.stringify(change.before)
                    && real[j].event.actorId === change.event.actorId
                    && (new Date(real[j].event.timestamp) - new Date(change.event.timestamp)) <= REVERSAL_WINDOW_MS;
                if (undoes) {
                    paired.add(i);
                    paired.add(j);
                    break;
                }
            }
        });

        summary.push({
            field,
            firstValue: real.length ? real[0].before : null,
            currentValue: real.length ? real[real.length - 1].after : null,
            changeCount: real.length,
            materialChangeCount: real.length - paired.size,
            noOpCount: changes.length - real.length,
            actors: [...new Set(real.map(c => c.event.actorName || c.event.actorId).filter(Boolean))]
        });
    }
    return summary;
}

function EventLine({ event }) {
    return (
        <li className="border-b border-gray-100 py-2">
            <div className="flex justify-between gap-2 text-xs text-gray-500">
                <span>{new Date(event.timestamp).toLocaleString('en-GB')}</span>
                <span className="font-mono">{event.action}</span>
            </div>
            <div className="text-sm text-gray-800">
                {event.actorName || event.actorLogin || event.actorId || 'unattributed'}
                {event.via && <span className="text-gray-400"> · {event.via}</span>}
                {event.noOp && <span className="ml-2 text-xs text-gray-500">(no-op)</span>}
            </div>
            {(event.fields || []).map(change => (
                <div key={change.field} className="text-xs text-gray-600 font-mono">
                    {change.field}: {displayValue(change.before, change.beforeLabel)} → {displayValue(change.after, change.afterLabel)}
                </div>
            ))}
            {isAccessRow(event) && (
                <div className="text-xs text-gray-600">
                    read {event.entityId} · {event.metadata?.outcome || 'success'}
                </div>
            )}
        </li>
    );
}

/**
 * Which pool an actor lives in, and what Cognito knows them as.
 *
 * The admin pool has no email alias, so an admin is looked up by sub or by the
 * console-typed username. A client's Cognito username is their email; their
 * `actorId` is a `lead_…` id, which Cognito has never heard of.
 */
export function cognitoSubjectOf(event) {
    if (!event) return null;
    if (event.actorType === 'admin') {
        const username = event.actorId || event.actorLogin;
        return username ? { pool: 'admin', username } : null;
    }
    return event.actorLogin ? { pool: 'client', username: event.actorLogin } : null;
}

/**
 * Cognito's own account state and sign-in record for one person.
 *
 * ⚠️ `available: false` IS NOT AN EMPTY HISTORY. Threat protection is PLUS-tier
 * and prod-only, so on preview and dev this comes back unavailable - and it is
 * rendered as the sentence it is, never as an empty table that would read as
 * "this person has never signed in".
 */
function AccountSection({ cognitoSubject }) {
    const [state, setState] = useState({ loading: true });

    useEffect(() => {
        if (!cognitoSubject) {
            setState({ loading: false, unresolvable: true });
            return undefined;
        }

        let cancelled = false;
        setState({ loading: true });

        (async () => {
            try {
                const { pool, username } = cognitoSubject;
                const response = await apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/audit/signins/${pool}/${encodeURIComponent(username)}`
                );
                const body = await response.json().catch(() => ({}));
                if (cancelled) return;
                setState(response.ok
                    ? { loading: false, data: body }
                    : { loading: false, error: body.error || `Lookup failed (${response.status})` });
            } catch (err) {
                if (!cancelled) setState({ loading: false, error: err.message });
            }
        })();

        return () => { cancelled = true; };
    }, [cognitoSubject]);

    if (state.unresolvable) {
        return (
            <p className="text-xs text-gray-500">
                No Cognito account can be resolved for this actor — the trail records
                no login for them.
            </p>
        );
    }
    if (state.loading) return <p className="text-xs text-gray-500">Asking Cognito…</p>;
    if (state.error) return <p className="text-xs text-red-700">{state.error}</p>;

    const {
        account, available, reason, events: signIns, cap, reachedCap, portal, pool, foundInPool
    } = state.data;
    const portalEvents = portal?.events || [];

    return (
        <div className="space-y-3">
            {/* ⚠️ A MISMATCH IS THE INTERESTING CASE, NOT AN ERROR. The audit row
                said one pool; the account lives in the other. That happens when
                somebody tries one portal's login with the other's address, which
                is precisely what a security review wants surfaced. */}
            {foundInPool && pool && foundInPool !== pool && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    No such account in the <strong>{pool}</strong> pool — this name belongs to
                    the <strong>{foundInPool}</strong> pool. A sign-in was attempted at the{' '}
                    {pool} portal using a {foundInPool} address.
                </p>
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><dt className="inline text-gray-500">Status: </dt><dd className="inline">{account.status || '—'}</dd></div>
                <div><dt className="inline text-gray-500">Enabled: </dt><dd className="inline">{String(account.enabled)}</dd></div>
                {/* ⚠️ NEVER a bare "none". A mandatory-MFA pool leaves the
                    per-user setting empty, so the old single field called an
                    admin who cannot sign in without an authenticator "none".
                    The state and its reason both come from the API. */}
                <div className="col-span-2">
                    <dt className="inline text-gray-500">MFA: </dt>
                    <dd className="inline" title={account.mfa?.detail || ''}>
                        {MFA_LABELS[account.mfa?.state] || 'unknown'}
                        {account.mfa?.detail && (
                            <span className="text-gray-500"> — {account.mfa.detail}</span>
                        )}
                    </dd>
                </div>
                <div><dt className="inline text-gray-500">Email: </dt><dd className="inline">{account.email || '—'}</dd></div>
                <div><dt className="inline text-gray-500">Created: </dt><dd className="inline">{account.created ? new Date(account.created).toLocaleDateString('en-GB') : '—'}</dd></div>
                <div><dt className="inline text-gray-500">Changed: </dt><dd className="inline">{account.lastModified ? new Date(account.lastModified).toLocaleDateString('en-GB') : '—'}</dd></div>
            </dl>

            {/* ⚠️ THIS SYSTEM'S OWN RECORD FIRST, because it is the one that
                always has data. Cognito's richer list is added below where the
                adaptive-auth add-on is on. The two are labelled and never
                merged, so a reader can tell which source an entry came from. */}
            <div>
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                    Sign-ins recorded by this system ({portalEvents.length})
                </h4>
                <ul className="text-xs">
                    {portalEvents.map(event => (
                        <li key={event.auditId} className="border-t border-gray-100 py-1">
                            <span className="text-gray-500">
                                {new Date(event.timestamp).toLocaleString('en-GB')}
                            </span>{' '}
                            <span className={event.outcome === 'failed' ? 'text-red-700' : 'text-green-700'}>
                                {event.outcome}
                            </span>
                            {event.reason && <span className="text-gray-500"> · {event.reason}</span>}
                            {event.mfaUsed && <span className="text-gray-500"> · MFA used</span>}
                            {event.firstLogin && <span className="text-gray-500"> · first login</span>}
                        </li>
                    ))}
                    {portalEvents.length === 0 && (
                        <li className="text-gray-500 py-1">
                            No sign-in recorded for this account. Sign-in capture began 2026-08-16;
                            anything earlier was never recorded.
                        </li>
                    )}
                </ul>
            </div>

            <h4 className="text-xs font-medium text-gray-700 mb-1">
                Cognito's own record{available ? ` (${signIns.length})` : ''}
            </h4>
            {available ? (
                <>
                    <ul className="text-xs">
                        {signIns.map(signIn => (
                            <li key={signIn.eventId} className="border-t border-gray-100 py-1">
                                <span className="text-gray-500">
                                    {new Date(signIn.timestamp).toLocaleString('en-GB')}
                                </span>{' '}
                                <span className={signIn.response === 'Pass' ? 'text-green-700' : 'text-red-700'}>
                                    {signIn.eventType} {signIn.response}
                                </span>
                                {signIn.context && (
                                    <span className="text-gray-500">
                                        {' '}· {signIn.context.ipAddress}
                                        {signIn.context.country ? ` (${signIn.context.country})` : ''}
                                        {signIn.context.device ? ` · ${signIn.context.device}` : ''}
                                    </span>
                                )}
                                {signIn.risk?.level && (
                                    <span className="text-amber-700"> · risk {signIn.risk.level}</span>
                                )}
                            </li>
                        ))}
                        {signIns.length === 0 && (
                            <li className="text-gray-500 py-1">Cognito has recorded no sign-in events.</li>
                        )}
                    </ul>
                    {reachedCap && (
                        <p className="text-xs text-amber-700">
                            Showing the most recent {cap} sign-in events — there are more.
                        </p>
                    )}
                </>
            ) : (
                <p className="text-xs text-amber-700">
                    {reason === 'advanced_security_not_enabled'
                        ? 'Cognito is not keeping a record of sign-ins here, so there is nothing '
                          + 'to show. It only keeps them when adaptive authentication is switched '
                          + 'on, which is prod only.'
                        : 'Sign-in records could not be read just now — this is a lookup failure, '
                          + 'not an empty history.'}
                </p>
            )}
        </div>
    );
}

/**
 * The trail of one thing: the raw-log warning, what stuck, then every event.
 *
 * ⚠️ ONE RENDERER, TWO PLACES. The slide-over on the audit page and the History
 * section in TaskDetailsModal show the same thing, and a second copy would drift
 * - most likely in the warning, which is the part that stops a reader quoting an
 * event count as a change count.
 */
export function AuditEventList({ events, windowLabel }) {
    const summary = useMemo(() => summarise(events), [events]);

    return (
        <>
            <div className="mx-4 my-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                A raw event log, not a narrative. Consecutive entries can be one editing
                session, and a value can move and move back within minutes — an event
                count is not a change count. Counts cover {windowLabel}.
            </div>

            {summary.length > 0 && (
                <CollapsibleSection id="summary" title="Summary of changes" count={summary.length}>
                    <table className="w-full text-xs">
                        <thead className="text-gray-500 text-left">
                            <tr>
                                <th className="py-1">Field</th>
                                <th className="py-1">First</th>
                                <th className="py-1">Now</th>
                                {/* Events: every change recorded. Net: what is left once
                                    no-ops and same-person reversals inside 30 minutes are
                                    discounted. The gap between them is the point - eleven
                                    due-date events on one live subtask are one afternoon
                                    of dragging a bar, not eleven reschedules. */}
                                <th className="py-1 text-right" title="Every change recorded">
                                    Events
                                </th>
                                <th
                                    className="py-1 text-right"
                                    title="What is left once no-ops and same-person reversals within 30 minutes are discounted"
                                >
                                    Net
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.map(row => (
                                <tr key={row.field} className="border-t border-gray-100">
                                    <td className="py-1 font-mono">{row.field}</td>
                                    <td className="py-1">{displayValue(row.firstValue)}</td>
                                    <td className="py-1">{displayValue(row.currentValue)}</td>
                                    <td className="py-1 text-right">{row.changeCount}</td>
                                    <td className="py-1 text-right font-medium">{row.materialChangeCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CollapsibleSection>
            )}

            <CollapsibleSection id="events" title="Every recorded event" count={events.length}>
                {events.length === 0 ? (
                    <p className="text-xs text-gray-500">Nothing recorded in this window.</p>
                ) : (
                    <ul>{events.map(event => <EventLine key={event.auditId} event={event} />)}</ul>
                )}
            </CollapsibleSection>
        </>
    );
}

function AuditEntityPanel({ subject, events, windowLabel, onClose }) {
    const cognitoSubject = useMemo(
        () => (subject?.kind === 'actor' ? cognitoSubjectOf(events[0]) : null),
        [subject, events]
    );

    if (!subject) return null;

    return (
        <aside className="fixed inset-y-0 right-0 w-[32rem] max-w-full bg-white shadow-xl border-l border-gray-200 flex flex-col z-20">
            <header className="flex items-start justify-between px-4 py-3 border-b border-gray-200">
                <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                        {subject.kind === 'actor' ? 'Person' : 'Entity'}
                    </p>
                    <h2 className="text-lg font-medium text-gray-900">{subject.title}</h2>
                    {subject.subtitle && (
                        <p className="text-sm text-gray-500 font-mono">{subject.subtitle}</p>
                    )}
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">
                    ×
                </button>
            </header>

            <div className="flex-1 overflow-y-auto">
                {subject.kind === 'actor' && (
                    <CollapsibleSection id="cognito" title="Account and sign-ins">
                        <AccountSection cognitoSubject={cognitoSubject} />
                    </CollapsibleSection>
                )}

                <AuditEventList events={events} windowLabel={windowLabel} />
            </div>
        </aside>
    );
}

export default AuditEntityPanel;
